import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Container, Table, Button, Row, Col, Input, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label } from 'reactstrap';
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import './UserType.css';
import Select from "react-select";
import { AuthContext } from '../../context/AuthContext';
import { ContaReceberContext } from '../../context/ContaReceberContext';
import { ContaFinanceiraContext } from '../../context/ContaFinanceiraContext';
import { CategoriaFinanceiraContext } from '../../context/CategoriaFinanceiraContext';
import { FormaPagamentoContext } from '../../context/FormaPagamentoContext';
import { ContaReceberType, CreateContaReceberType, UpdateContaReceberType, EfetuarBaixaReceberRequest } from '../../context/hooks/useContaReceber';
import useUser from '../../context/hooks/useUser';
import { createPermissionChecker, PermissionModule } from '../../types/permissions';

import { CreateContaReceberModal } from '../../components/ContaReceber/CreateContaReceberModal';
import { EditContaReceberModal } from '../../components/ContaReceber/EditContaReceberModal';

const ContaReceber = () => {
    const { objUserTk } = useContext(AuthContext);
    const { listContaReceber, getAllContaReceberPaged, createContaReceber, updateContaReceber, deleteContaReceber, efetuarBaixaContaReceber, efetuarBaixaLoteContaReceber, contaReceberTotalValor } = useContext(ContaReceberContext);
    const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
    const [expandedVendas, setExpandedVendas] = useState<Set<number>>(new Set());
    const { handleGetContaFinanceiraByEmpresaId, listContaFinanceiraByEmpresa } = useContext(ContaFinanceiraContext);
    const { handleGetCategoriaFinanceiraByEmpresaId, listCategoriaFinanceiraByEmpresa } = useContext(CategoriaFinanceiraContext);
    const { handleGetFormaPagamentoByEmpresaId, listFormaPagamentoByEmpresa } = useContext(FormaPagamentoContext);
    const { getPermissionsByUser, userPermissions, useLegacyPermissions, modulePermissions } = useUser();
    const hasPermission = createPermissionChecker(useLegacyPermissions, userPermissions, modulePermissions);
    const [editingContaReceber, setEditingContaReceber] = useState<UpdateContaReceberType | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [groupPage, setGroupPage] = useState(1);

    // Paginação real acontece aqui no cliente, sobre as linhas JÁ AGRUPADAS por
    // venda — não teria como paginar certo no servidor sobre linhas cruas, porque
    // uma venda de 20 parcelas pode ocupar várias "páginas" cruas e virar 1 linha
    // resumo em cada uma (ex.: página 2 mostrando só 1 registro). Por isso sempre
    // buscamos um lote grande do backend (RAW_FETCH_SIZE) de uma vez.
    const RAW_FETCH_SIZE = 500;

    const [filters, setFilters] = useState({
        dataInicial: '',
        dataFinal: '',
        status: '',
        codigoTransacao: '',
        documento: '',
        historico: '',
        contaFinanceiraId: '',
        categoriaFinanceiraId: '',
        formaPagamentoId: ''
    });

    useEffect(() => {
        setLoading(true);

        handleGetCategoriaFinanceiraByEmpresaId(objUserTk.EmpresaId);
        handleGetContaFinanceiraByEmpresaId(objUserTk.EmpresaId);
        handleGetFormaPagamentoByEmpresaId(objUserTk.EmpresaId);

        getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId)
            .then(data => {
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao buscar contas:", error);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (objUserTk?.UserId) {
                const permissions = await getPermissionsByUser(objUserTk.UserId);
            }
        };

        fetchPermissions().catch(error => console.error("Erro ao carregar permissões:", error));
    }, [objUserTk?.UserId]);

    // Troca de página é só um recorte client-side dos grupos já carregados —
    // não busca nada novo do backend (ver RAW_FETCH_SIZE acima).
    const onChangePaginationCommon = (e: any, value: any) => {
        setGroupPage(value);
    };


    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearch = () => {
        setLoading(true);
        setGroupPage(1);
        getAllContaReceberPaged(
            1,
            RAW_FETCH_SIZE,
            objUserTk.EmpresaId,
            filters.dataInicial,
            filters.dataFinal,
            filters.status !== '' ? Number(filters.status) : undefined,
            filters.codigoTransacao ? Number(filters.codigoTransacao) : undefined,
            filters.documento || undefined,
            filters.historico || undefined,
            filters.contaFinanceiraId ? Number(filters.contaFinanceiraId) : undefined,
            filters.categoriaFinanceiraId ? Number(filters.categoriaFinanceiraId) : undefined,
            filters.formaPagamentoId ? Number(filters.formaPagamentoId) : undefined
        )
            .then(() => {
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao aplicar filtros:", error);
                setLoading(false);
            });
    };

    const handleClearFilters = () => {
        setFilters({
            dataInicial: '',
            dataFinal: '',
            status: '',
            codigoTransacao: '',
            documento: '',
            historico: '',
            contaFinanceiraId: '',
            categoriaFinanceiraId: '',
            formaPagamentoId: ''
        });
        setLoading(true);
        setGroupPage(1);
        getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId)
            .then(() => {
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao liimpar filtros:", error);
                setLoading(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleEditContaReceber = (contaReceber: ContaReceberType) => {
        setEditingContaReceber(contaReceber);
        setEditModalOpen(true);
    };

    const handleDeleteContaReceber = (contaReceberId: number) => {
        if (window.confirm("Tem certeza que deseja excluir esta conta?")) {
            deleteContaReceber(contaReceberId)
                .then(deletedContaReceber => {
                    setGroupPage(1);
                    if (deletedContaReceber) {
                        getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId);
                    } else {
                        console.error("Erro ao excluir conta.");
                    }
                    getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId);
                })
                .catch(error => {
                    console.error("Erro ao excluir conta:", error);
                });
        }
    };

    const PARCELAS_VISIVEIS_INICIAL = 5;
    const [visibleCounts, setVisibleCounts] = useState<Record<number, number>>({});

    const toggleExpandVenda = (vendaId: number) => {
        setExpandedVendas(prev => {
            const next = new Set(prev);
            if (next.has(vendaId)) {
                next.delete(vendaId);
            } else {
                next.add(vendaId);
                // toda vez que expande de novo, começa mostrando só as 5 primeiras —
                // evita lembrar um "mostrar todas" anterior e voltar a poluir a tela.
                setVisibleCounts(vc => ({ ...vc, [vendaId]: PARCELAS_VISIVEIS_INICIAL }));
            }
            return next;
        });
    };

    const showMoreParcelas = (vendaId: number, total: number) => {
        setVisibleCounts(prev => ({
            ...prev,
            [vendaId]: Math.min((prev[vendaId] ?? PARCELAS_VISIVEIS_INICIAL) + PARCELAS_VISIVEIS_INICIAL, total),
        }));
    };

    // Agrupa itens CONSECUTIVOS com o mesmo vendaId (mesma noção de "grupo" que já
    // era usada pra desenhar a borda separadora) — parceladas de uma venda (ex.: 20x
    // boleto) viram 1 grupo, pra poder colapsar em 1 linha resumo na tabela.
    const gruposContaReceber = useMemo(() => {
        const grupos: ContaReceberType[][] = [];
        for (const item of listContaReceber) {
            const grupoAtual = grupos[grupos.length - 1];
            if (grupoAtual && item.vendaId != null && grupoAtual[0].vendaId === item.vendaId) {
                grupoAtual.push(item);
            } else {
                grupos.push([item]);
            }
        }
        return grupos;
    }, [listContaReceber]);

    // Paginação client-side sobre os GRUPOS (não sobre listContaReceber cru) — cada
    // "página" mostrada ao usuário sempre tem sentido, mesmo que uma venda parcelada
    // esconda dezenas de linhas cruas dentro de 1 grupo só.
    const groupPageCount = Math.max(1, Math.ceil(gruposContaReceber.length / pageSize));
    const gruposDaPagina = useMemo(
        () => gruposContaReceber.slice((groupPage - 1) * pageSize, groupPage * pageSize),
        [gruposContaReceber, groupPage, pageSize]
    );

    const toggleSelecionada = (id: number, status: number) => {
        if (status === 1) return; // não seleciona conta já concluída
        setSelecionadas(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleBaixaLote = () => {
        if (selecionadas.size === 0) return;
        if (!window.confirm(`Dar baixa em ${selecionadas.size} conta(s) selecionada(s)?`)) return;

        efetuarBaixaLoteContaReceber(Array.from(selecionadas), new Date().toISOString())
            .then(success => {
                if (success) {
                    setSelecionadas(new Set());
                    setGroupPage(1);
                    getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId);
                }
            })
            .catch(error => console.error("Erro ao efetuar baixa em lote:", error));
    };

    const colorMapByDay: { [key: number]: string } = {
        1: "#FFEBEE",
        2: "#E3F2FD",
        3: "#E8F5E9",
        4: "#FFF3E0",
        5: "#F3E5F5",
        6: "#FBE9E7",
        7: "#E0F2F1",
        8: "#FFF8E1",
        9: "#EDE7F6",
        10: "#ECEFF1",
        11: "#FFCDD2",
        12: "#BBDEFB",
        13: "#C8E6C9",
        14: "#FFE0B2",
        15: "#D1C4E9",
        16: "#FFCCBC",
        17: "#B2DFDB",
        18: "#FFECB3",
        19: "#D7CCC8",
        20: "#CFD8DC",
        21: "#F48FB1",
        22: "#90CAF9",
        23: "#A5D6A7",
        24: "#FFAB91",
        25: "#CE93D8",
        26: "#FF8A65",
        27: "#80CBC4",
        28: "#FFD54F",
        29: "#BCAAA4",
        30: "#B0BEC5",
        31: "#F06292"
    };

    const getColorByDay = (date: string): string => {
        const day = new Date(date).getDate();
        return colorMapByDay[day] || "#FFFFFF";
    };

    const renderContaReceberRow = (
        contaReceber: ContaReceberType,
        options?: { borderTop?: boolean; parcelaCell?: React.ReactNode }
    ) => {
        const rowColor = getColorByDay(contaReceber.vencimento);

        return (
            <tr
                key={contaReceber.id}
                style={{
                    backgroundColor: rowColor,
                    borderTop: options?.borderTop ? '2px solid #8898aa' : undefined,
                }}
            >
                <td>
                    {contaReceber.status !== 1 && (
                        <input
                            type="checkbox"
                            checked={selecionadas.has(contaReceber.id)}
                            onChange={() => toggleSelecionada(contaReceber.id, contaReceber.status)}
                        />
                    )}
                </td>
                <td>{contaReceber.id}</td>
                <td>{contaReceber.codigoTransacao}</td>
                <td>{contaReceber.documento}</td>
                <td>
                    {contaReceber.alunoNome && (
                        <><strong>Aluno {contaReceber.alunoNome}</strong><br /></>
                    )}
                    {contaReceber.historico}
                </td>
                <td>
                    {options?.parcelaCell !== undefined ? options.parcelaCell : (
                        contaReceber.totalParcelas > 1 ? (
                            <span className="badge badge-info">
                                {contaReceber.numeroParcela}/{contaReceber.totalParcelas}
                            </span>
                        ) : '-'
                    )}
                </td>
                <td>{new Date(contaReceber.dataEmissao).toLocaleDateString('pt-BR')}</td>
                <td>{contaReceber.vencimento && contaReceber.vencimento.split('-').reverse().join('/')}</td>
                <td>{contaReceber.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                <td>{contaReceber.taxa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                <td>
                    {contaReceber.status === 0 && <span className="badge badge-warning">Pendente</span>}
                    {contaReceber.status === 1 && <span className="badge badge-success">Concluído</span>}
                </td>
                <td>{contaReceber.contaFinanceiraName}</td>
                <td>{contaReceber.categoriaFinanceiraName}</td>
                <td>{contaReceber.formaPagamentoName}</td>
                <td>
                    {hasPermission(PermissionModule.Financeiro, 3) && (
                        <Button onClick={() => { handleEditContaReceber(contaReceber) }}><i className="fa-solid fa-pen-to-square"></i></Button>
                    )}
                    {hasPermission(PermissionModule.Financeiro, 4) && (
                        <Button onClick={() => handleDeleteContaReceber(contaReceber.id)} color="danger"><i className="fa-solid fa-trash"></i></Button>
                    )}
                    {contaReceber.status !== 1 && (
                        <Button
                            onClick={() => {
                                const request: EfetuarBaixaReceberRequest = {
                                    contaReceberId: contaReceber.id,
                                    dataBaixa: new Date().toISOString(),
                                    valorPago: contaReceber.valor,
                                };
                                efetuarBaixaContaReceber(request)
                                    .then(success => {
                                        if (success) {
                                            setGroupPage(1);
                                            getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId);
                                        } else {
                                            console.error("Erro ao efetuar baixa na conta.");
                                        }
                                    })
                                    .catch(error => console.error("Erro ao efetuar baixa:", error));
                            }}
                        >
                            <i className="fa-solid fa-check"></i>
                        </Button>
                    )}
                </td>
            </tr>
        );
    };

    const listCategoriaFinanceiraOptions = listCategoriaFinanceiraByEmpresa.map(categoriaFinanceira => ({
        value: categoriaFinanceira.id,
        label: categoriaFinanceira.name,
    }));

    const listContaFinanceiraOptions = listContaFinanceiraByEmpresa.map(contaFinanceira => ({
        value: contaFinanceira.id,
        label: contaFinanceira.name,
    }));

    const listFormaPagamentoOptions = listFormaPagamentoByEmpresa.map(forma => ({
        value: forma.id,
        label: forma.name
    }));

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar
                brandText={"Conta Receber"}
                search={false}
                titleSearch={""}
            />

            <Container className="mt-5">
                <Row className="align-items-center mb-2">
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Data Inicial</Label>
                        <Input
                            className="form-control-alternative"
                            type="date"
                            name="dataInicial"
                            value={filters.dataInicial}
                            onChange={handleFilterChange}
                        />
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Data Final</Label>
                        <Input
                            className="form-control-alternative"
                            type="date"
                            name="dataFinal"
                            value={filters.dataFinal}
                            onChange={handleFilterChange}
                        />
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Status</Label>
                        <Input
                            className="form-control-alternative"
                            type="select"
                            name="status"
                            value={filters.status}
                            onChange={handleFilterChange}
                        >
                            <option value="">Todos</option>
                            <option value="0">Pendente</option>
                            <option value="1">Concluído</option>
                        </Input>
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Código Transação</Label>
                        <Input
                            className="form-control-alternative"
                            type="text"
                            name="codigoTransacao"
                            value={filters.codigoTransacao}
                            onChange={handleFilterChange}
                            placeholder="Código da transação"
                        />
                    </Col>
                </Row>

                <Row className="align-items-center mb-2">
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Documento</Label>
                        <Input
                            className="form-control-alternative"
                            type="text"
                            name="documento"
                            value={filters.documento}
                            onChange={handleFilterChange}
                            placeholder="Número do documento"
                        />
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Observação</Label>
                        <Input
                            className="form-control-alternative"
                            type="text"
                            name="historico"
                            value={filters.historico}
                            onChange={handleFilterChange}
                            placeholder="Buscar pela observação"
                        />
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Conta Financeira</Label>
                        <Select
                            className="form-control-alternative"
                            options={listContaFinanceiraOptions}
                            value={listContaFinanceiraOptions.find(opt => opt.value.toString() === filters.contaFinanceiraId)}
                            onChange={(selected) =>
                                setFilters(prev => ({ ...prev, contaFinanceiraId: selected?.value.toString() || '' }))
                            }
                            isClearable
                            placeholder="Selecione uma conta financeira"
                        />
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Categoria Financeira</Label>
                        <Select
                            className="form-control-alternative"
                            options={listCategoriaFinanceiraOptions}
                            value={listCategoriaFinanceiraOptions.find(opt => opt.value.toString() === filters.categoriaFinanceiraId)}
                            onChange={(selected) =>
                                setFilters(prev => ({ ...prev, categoriaFinanceiraId: selected?.value.toString() || '' }))
                            }
                            isClearable
                            placeholder="Selecione uma categoria"
                        />
                    </Col>
                    <Col xs="12" md="3" className="mb-2">
                        <Label className="form-control-label">Forma de Pagamento</Label>
                        <Select
                            className="form-control-alternative"
                            options={listFormaPagamentoOptions}
                            value={listFormaPagamentoOptions.find(opt => opt.value.toString() === filters.formaPagamentoId)}
                            onChange={(selected) =>
                                setFilters(prev => ({ ...prev, formaPagamentoId: selected?.value.toString() || '' }))
                            }
                            isClearable
                            placeholder="Selecione a forma de pagamento"
                        />
                    </Col>
                </Row>

                <Row className="align-items-center mb-4">
                    <Col xs="12" className="d-flex justify-content-start justify-content-md-end">
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#585858" }}
                            onClick={handleSearch}
                            className="mr-2"
                        >
                            Buscar
                        </Button>
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                            onClick={handleClearFilters}
                        >
                            Limpar
                        </Button>
                    </Col>
                    {hasPermission(PermissionModule.Financeiro, 3) && selecionadas.size > 0 && (
                        <Col xs="12" className="text-right mt-3">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#2dce89" }}
                                onClick={handleBaixaLote}
                            >
                                <i className="fa-solid fa-check mr-2"></i>
                                Dar baixa em {selecionadas.size} selecionada(s)
                            </Button>
                        </Col>
                    )}
                    {hasPermission(PermissionModule.Financeiro, 1) && (
                        <Col xs="12" className="text-right mt-3">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                className="btn-filter"
                                onClick={() => setCreateModalOpen(true)}
                            >
                                Adicionar conta
                            </Button>
                        </Col>
                    )}
                </Row>

                {hasPermission(PermissionModule.Financeiro, 2) ? (
                    <Row className="mt-4">
                        <Col>
                            <Table hover responsive className="table-striped custom-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Sequência</th>
                                        <th>Código Transação</th>
                                        <th>Documento</th>
                                        <th>Observação</th>
                                        <th>Parcela</th>
                                        <th>Data Emissão</th>
                                        <th>Vencimento</th>
                                        <th>Valor</th>
                                        <th>Taxa</th>
                                        <th>Status</th>
                                        <th>Conta</th>
                                        <th>Cateforia</th>
                                        <th>Forma Pgmt.</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gruposDaPagina.map((grupo) => {
                                        const vendaId = grupo[0].vendaId;
                                        const isParcelado = grupo.length > 1 && vendaId != null;
                                        const expanded = isParcelado && expandedVendas.has(vendaId as number);

                                        if (isParcelado && !expanded) {
                                            // Resumo: mostra a próxima parcela em aberto (ou a última, se
                                            // todas já concluídas) e esconde o resto até o usuário expandir.
                                            const destaque = grupo.find(c => c.status !== 1) ?? grupo[grupo.length - 1];
                                            return renderContaReceberRow(destaque, {
                                                borderTop: true,
                                                parcelaCell: (
                                                    <span
                                                        className="badge badge-info"
                                                        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                        onClick={() => toggleExpandVenda(vendaId as number)}
                                                        title={`Ver todas as ${grupo.length} parcelas`}
                                                    >
                                                        {destaque.numeroParcela}/{destaque.totalParcelas}{' '}
                                                        <i className="fa-solid fa-angles-down"></i>
                                                    </span>
                                                ),
                                            });
                                        }

                                        if (isParcelado) {
                                            const visibleCount = visibleCounts[vendaId as number] ?? PARCELAS_VISIVEIS_INICIAL;
                                            const visiveis = grupo.slice(0, visibleCount);
                                            const restantes = grupo.length - visiveis.length;

                                            const linhas = visiveis.map((contaReceber, i) => renderContaReceberRow(contaReceber, {
                                                borderTop: i === 0,
                                                parcelaCell: (
                                                    <span
                                                        className="badge badge-info"
                                                        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                        onClick={() => toggleExpandVenda(vendaId as number)}
                                                        title="Recolher parcelas"
                                                    >
                                                        {contaReceber.numeroParcela}/{contaReceber.totalParcelas}
                                                        {i === 0 && <> <i className="fa-solid fa-angles-up"></i></>}
                                                    </span>
                                                ),
                                            }));

                                            if (restantes > 0) {
                                                linhas.push(
                                                    <tr key={`mostrar-mais-${vendaId}`}>
                                                        <td colSpan={15} className="text-center">
                                                            <Button
                                                                size="sm"
                                                                color="link"
                                                                onClick={() => showMoreParcelas(vendaId as number, grupo.length)}
                                                            >
                                                                Mostrar mais {Math.min(PARCELAS_VISIVEIS_INICIAL, restantes)} (faltam {restantes} de {grupo.length})
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return linhas;
                                        }

                                        return grupo.map((contaReceber, i) => renderContaReceberRow(contaReceber, {
                                            borderTop: i === 0 && vendaId != null,
                                        }));
                                    })}
                                </tbody>
                            </Table>
                            <div className="text-right mt-3">
                                <strong>
                                    Total das contas filtradas:&nbsp;
                                    {typeof contaReceberTotalValor === "number"
                                        ? contaReceberTotalValor.toLocaleString("pt-BR", {
                                            style: "currency",
                                            currency: "BRL",
                                        })
                                        : "R$ 0,00"}
                                </strong>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                                <div className="mb-2 mb-md-0">
                                    <Label className="form-control-label mr-2">Registros por página:</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="select"
                                        bsSize="sm"
                                        style={{ width: "100px", display: "inline-block" }}
                                        value={pageSize}
                                        onChange={(e) => {
                                            // Só reparticiona os grupos já carregados — não busca nada
                                            // novo do backend (ver RAW_FETCH_SIZE acima).
                                            setPageSize(parseInt(e.target.value));
                                            setGroupPage(1);
                                        }}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={15}>15</option>
                                        <option value={20}>20</option>
                                        <option value={25}>25</option>
                                    </Input>
                                </div>

                                {groupPageCount > 1 && (
                                    <div className="mt-2 mt-md-0">
                                        <PaginationMaterial
                                            onChange={(e: any, value: any) => onChangePaginationCommon(e, value)}
                                            count={groupPageCount}
                                            page={groupPage}
                                        />
                                    </div>
                                )}

                                {listContaReceber.length === 0 && (
                                    <div className="w-100 mt-3" style={{ color: "red" }}>
                                        NÃO FOI ENCONTRADO NENHUM REGISTRO
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="mt-4">
                        <Col>
                            <div style={{ color: "red", textAlign: "center" }}>
                                Você não tem permissão para visualizar as contas do financeiro.
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>

            <CreateContaReceberModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onCreated={() => { setGroupPage(1); getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId); }}
            />

            <EditContaReceberModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(false)}
                conta={editingContaReceber}
                onUpdated={() => { setGroupPage(1); getAllContaReceberPaged(1, RAW_FETCH_SIZE, objUserTk.EmpresaId); }}
            />
        </>
    );
};

export default ContaReceber;